import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightSvg } from '~/svg';
import { fetchTopCampaigns } from '~/services/dashboard';

export type TopCampaign = {
    campaign_id: number;
    client_name: string;
    donor_count: number;
    donations: number;
};

const TopCampaigns = () => {
    const [campaigns, setCampaigns] = useState<TopCampaign[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadTopCampaigns = async () => {
            try {
                const data = await fetchTopCampaigns();
                if (Array.isArray(data)) {
                    setCampaigns(data);
                } else {
                    console.error('Top campaigns API returned non-array:', data);
                    setCampaigns([]);
                }
            } catch (error) {
                console.error('Error fetching top campaigns:', error);
                setCampaigns([]);
            } finally {
                setLoading(false);
            }
        };

        loadTopCampaigns();
    }, []);

    const maxDonations = campaigns.length > 0
        ? Math.max(...campaigns.map((c) => c.donations))
        : 0;

    return (
        <div className='h-full rounded-sm border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark md:p-6 xl:p-7.5'>
            <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                <h2 className='text-xl font-semibold text-black dark:text-white'>
                    Top Campaigns
                </h2>

                <Link
                    to='/client-campaigns'
                    className='flex items-center gap-2 text-primary'
                >
                    View All <ArrowRightSvg />
                </Link>
            </div>

            {loading ? (
                <div className='flex items-center justify-center py-8'>
                    <p className='text-secondary'>Loading...</p>
                </div>
            ) : campaigns.length === 0 ? (
                <div className='flex items-center justify-center py-8'>
                    <p className='text-secondary'>No campaigns found</p>
                </div>
            ) : (
                <div className='flex flex-col gap-2'>
                    <div className='grid grid-cols-10 py-2'>
                        <div className='col-span-6'>
                            <p className='text-sm text-secondary'>Client</p>
                        </div>
                        <div className='col-span-2'>
                            <p className='text-sm text-secondary'>
                                Donors
                            </p>
                        </div>
                        <div className='col-span-2'>
                            <p className='text-sm text-secondary'>
                                Donations
                            </p>
                        </div>
                    </div>
                    {campaigns.map((campaign) => {
                        const barWidth = maxDonations > 0
                            ? (campaign.donations / maxDonations) * 74
                            : 0;
                        
                        return (
                            <Link
                                key={campaign.campaign_id}
                                to={`/campaigns/${campaign.campaign_id}`}
                                className='relative z-1 grid grid-cols-10 py-2 transition-opacity hover:opacity-75'
                            >
                                <span 
                                    className='absolute left-0 top-0 -z-1 h-full rounded-lg bg-primary bg-opacity-20 dark:bg-meta-4'
                                    style={{ width: `${barWidth}%` }}
                                ></span>
                                <div className='col-span-6 pl-3.5'>
                                    <p>{campaign.client_name || 'N/A'}</p>
                                </div>
                                <div className='col-span-2'>
                                    <p>{campaign.donor_count.toLocaleString()}</p>
                                </div>
                                <div className='col-span-2'>
                                    <p>
                                        ${campaign.donations.toLocaleString('en-US', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })}
                                    </p>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default TopCampaigns;
